import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { hasAnyPermission, permissionSet, withPanelBase } from "@/lib/authz";
import { api } from "@/lib/api";
import {
  Boxes,
  Package,
  Plug,
  Warehouse,
  BarChart3,
  Users,
  Settings,
  Wrench,
  LogOut,
  X,
  ChevronLeft,
  ChevronRight,
  FileText,
  ClipboardList,
  BadgeDollarSign,
  Building2,
  Gift,
  FileDown,
  CreditCard,
  Grid3X3,
  LifeBuoy,
  RefreshCw,
  Truck,
  ShoppingCart,
  Wallet,
  Landmark,
  Sparkles,
  Tag,
} from "lucide-react";

const NAV = [
  {
    title: null,
    items: [
      { key: "overview", label: "Genel Bakış", to: "/panel", icon: Boxes, end: true },
      { key: "products", label: "Ürünler", to: "/panel/urunler", icon: Package, requiredAny: ["catalog.view", "catalog.manage"] },
      { key: "categories", label: "Kategoriler", to: "/panel/kategoriler", icon: Tag, requiredAny: ["catalog.view", "catalog.manage"] },
      { key: "integrations", label: "Entegrasyonlar", to: "/panel/entegrasyonlar", icon: Plug, requiredAny: ["integrations.view", "integrations.manage"] },
    ],
  },
  {
    title: "Finans",
    items: [
      { key: "finance", label: "Finans Merkezi", to: "/panel/finans", icon: Landmark, requiredAny: ["billing.view", "billing.manage", "reports.view"] },
      { key: "customers", label: "Cari Hesaplar", to: "/panel/musteriler", icon: Users, requiredAny: ["customers.view", "customers.manage"] },
      { key: "kasa", label: "Kasa & Bankalar", to: "/panel/kasa", icon: Wallet, requiredAny: ["billing.view", "billing.manage"] },
      { key: "local-invoices", label: "Satış Faturaları", to: "/panel/satislar/fatura-olustur", icon: FileText, requiredAny: ["billing.view", "billing.manage"] },
      { key: "incoming-e-invoices", label: "Gelen Faturalar", to: "/panel/giderler/gelen-e-faturalar", icon: FileDown, requiredAny: ["billing.view", "billing.manage"] },
      { key: "reports", label: "Gelir & Gider", to: "/panel/raporlar", icon: BarChart3, requiredAny: ["reports.view"] },
    ],
  },
  {
    title: "Satışlar",
    items: [
      { key: "pos", label: "Satış Ekranı", to: "/panel/satis", icon: ShoppingCart, requiredAny: ["dashboard.view"] },
      { key: "invoices", label: "Giden Faturalar", to: "/panel/satislar/faturalar", icon: FileDown, requiredAny: ["billing.view", "billing.manage"] },
      { key: "teklifler", label: "Teklifler", to: "/panel/satislar/teklifler", icon: ClipboardList, requiredAny: ["billing.view", "billing.manage"] },
    ],
  },
  {
    title: "Operasyon",
    items: [
      { key: "product-stock", label: "Ürün Stok Listesi", to: "/panel/urun-stok", icon: ClipboardList, requiredAny: ["inventory.view", "inventory.manage"] },
      { key: "product-prices", label: "Ürün Fiyat Listesi", to: "/panel/urun-fiyat", icon: BadgeDollarSign, requiredAny: ["inventory.view", "inventory.manage"] },
      { key: "integration-jobs", label: "Entegrasyon İşlemleri", to: "/panel/urunler/islemler", icon: RefreshCw, requiredAny: ["catalog.view", "catalog.manage"] },
      { key: "ai-intelligence", label: "AI Ürün Zekası", to: "/panel/ai-intelligence", icon: Sparkles, requiredAny: ["catalog.view", "catalog.manage"] },
      { key: "hepsijet-manual-labels", label: "HepsiJet", to: "/panel/operasyon/hepsijet-etiket", icon: Truck, requiredAny: ["inventory.view", "inventory.manage"] },
      { key: "warehouses", label: "Stok & Depolar", to: "/panel/stok", icon: Warehouse, requiredAny: ["inventory.view", "inventory.manage"] },
    ],
  },
  {
    title: "Sistem",
    items: [
      { key: "settings", label: "Ayarlar", to: "/panel/ayarlar", icon: Settings, requiredAny: ["settings.view", "settings.manage"] },
      { key: "logs", label: "Loglar", to: "/panel/loglar", icon: Wrench, requiredAny: ["logs.view"] },
    ],
  },
  {
    title: "Hesabım",
    items: [
      { key: "my-companies", label: "Firmalarım", to: "/panel/hesabim/firmalarim", icon: Building2, requiredAny: ["dashboard.view"] },
      { key: "subscription", label: "Abonelik", to: "/panel/hesabim/abonelik", icon: CreditCard, requiredAny: ["dashboard.view"] },
      { key: "applications", label: "Uygulamalar", to: "/panel/uygulamalar", icon: Grid3X3, requiredAny: ["dashboard.view"] },
      { key: "billing", label: "Faturalandırma", to: "/panel/hesabim/faturalandirma", icon: CreditCard, requiredAny: ["dashboard.view"] },
      { key: "support-tickets", label: "Destek Taleplerim", to: "/panel/hesabim/destek-taleplerim", icon: LifeBuoy, requiredAny: ["dashboard.view"] },
      { key: "invite-earn", label: "Davet Et, Kazan", to: "/panel/hesabim/davet-et-kazan", icon: Gift, requiredAny: ["dashboard.view"] },
    ],
  },
];

export default function Sidebar({ onClose, expanded = false, showToggle = false, onToggleExpand }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [supportModuleEnabled, setSupportModuleEnabled] = useState(true);
  const [hepsiJetMenuEnabled, setHepsiJetMenuEnabled] = useState(false);
  const [accountingProvider, setAccountingProvider] = useState("");
  const permissions = permissionSet(user);
  const panelPath = (path) => withPanelBase(path, user, location.pathname);
  const filteredNav = NAV.map((section) => ({
    ...section,
    items: section.items.filter((item) => hasAnyPermission(permissions, item.requiredAny)),
  })).filter((section) => section.items.length > 0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let res;
        try {
          res = await api.get("/integration-providers");
        } catch (primaryErr) {
          res = await api.get("/integrations/providers");
        }
        if (cancelled) return;
        const rows = Array.isArray(res?.items) ? res.items : [];
        const enabled = rows.some((x) => String(x?.key || "").toLowerCase() === "support_center");
        setSupportModuleEnabled(enabled);
      } catch {
        if (!cancelled) setSupportModuleEnabled(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cfgRes, intgRes] = await Promise.all([
          api.get("/hepsijet/config").catch(() => null),
          api.get("/integrations").catch(() => []),
        ]);
        if (cancelled) return;
        const configured = Boolean(cfgRes?.configured);
        const integrations = Array.isArray(intgRes) ? intgRes : [];
        const hasIntegration = integrations.some((row) => {
          const provider = String(row?.provider || "").trim().toLowerCase();
          const status = String(row?.status || "").trim().toLowerCase();
          return provider === "hepsijet" && status !== "deleted";
        });
        setHepsiJetMenuEnabled(configured || hasIntegration);
      } catch {
        if (!cancelled) setHepsiJetMenuEnabled(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await api.get("/settings/accounting").catch(() => null);
        if (cancelled) return;
        const provider = String(cfg?.provider || "").trim().toLowerCase();
        setAccountingProvider(provider === "parasut" || provider === "bizimhesap" ? provider : "");
      } catch {
        if (!cancelled) setAccountingProvider("");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const visibleNav = filteredNav.map((section) => ({
    ...section,
    items: section.items.filter((item) => {
      if (item.key === "support-tickets") return supportModuleEnabled;
      if (item.key === "hepsijet-manual-labels") return hepsiJetMenuEnabled;
      if (item.key === "incoming-e-invoices" && accountingProvider === "bizimhesap") return false;
      return true;
    }),
  })).filter((section) => section.items.length > 0);

  const handleLogout = () => {
    logout();
    navigate("/giris");
  };

  return (
    <div className="flex flex-col h-full border-r border-white/[0.08] bg-[linear-gradient(180deg,#0b1220_0%,#0f172a_52%,#111a2e_100%)]">
      <div className={cn("relative h-16 flex items-center border-b border-white/[0.08]", expanded ? "justify-between px-5" : "justify-center px-3")}>
        <NavLink
          to={panelPath("/panel")}
          className={cn("flex items-center w-full", expanded ? "justify-start gap-2.5" : "justify-center")}
          onClick={onClose}
        >
          {expanded ? (
            <img src="/logomain.svg" alt="obihub" className="h-8 w-auto" />
          ) : (
            <img src="/obihub-icon.png" alt="obihub" className="h-9 w-9" />
          )}
        </NavLink>

        {showToggle && typeof onToggleExpand === "function" ? (
          <button
            type="button"
            className="hidden md:inline-flex absolute -right-3 h-6 w-6 rounded-full items-center justify-center bg-white text-[#1487a3] hover:bg-teal-50 border border-border shadow-sm"
            onClick={onToggleExpand}
            title={expanded ? "Menüyü daralt" : "Menüyü genişlet"}
            data-testid="sidebar-collapse-toggle"
          >
            {expanded ? <ChevronLeft className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
        ) : null}

        <button
          type="button"
          className="md:hidden absolute right-3 h-8 w-8 rounded-lg text-muted-foreground hover:text-white hover:bg-white/10 inline-flex items-center justify-center transition-colors"
          onClick={onClose}
          data-testid="sidebar-close-btn"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <nav className={cn("flex-1 overflow-y-auto sidebar-scrollbar py-4", expanded ? "px-3" : "px-2")}>
        {visibleNav.map((sec, si) => (
          <div key={si} className={cn("mb-6", si === visibleNav.length - 1 && "mb-0")}>
            {sec.title && expanded ? (
              <div className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                {sec.title}
              </div>
            ) : null}
            <div className="space-y-0.5">
              {sec.items.map((item) => (
                <NavLink
                  key={item.key}
                  to={panelPath(item.to)}
                  end={item.end}
                  onClick={onClose}
                  className={({ isActive }) =>
                    cn(
                      "group flex items-center rounded-lg py-2 text-[13px] transition-all duration-150",
                      expanded ? "gap-3" : "justify-center px-2",
                      isActive
                        ? expanded
                          ? "pl-[11px] pr-3 bg-white/[0.09] text-white font-semibold border-l-2 border-[#22c8e0]"
                          : "bg-[#1487a3]/20 text-[#5dd9e8] shadow-[inset_0_0_0_1px_rgba(93,217,232,0.25)]"
                        : expanded
                          ? "px-3 text-slate-400 hover:text-slate-200 hover:bg-white/[0.06]"
                          : "text-slate-400 hover:text-slate-200 hover:bg-white/[0.06]"
                    )
                  }
                  data-testid={`nav-${item.key}`}
                >
                  {({ isActive }) => (
                    <>
                      <item.icon
                        className={cn(
                          expanded ? "h-[18px] w-[18px] shrink-0" : "h-[22px] w-[22px] shrink-0",
                          isActive ? "text-[#22c8e0]" : "text-slate-500 group-hover:text-slate-300"
                        )}
                        strokeWidth={1.75}
                      />
                      {expanded ? <span className="font-medium flex-1">{item.label}</span> : null}
                      {expanded && isActive ? <span className="h-1.5 w-1.5 rounded-full bg-[#22c8e0] shrink-0" /> : null}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="border-t border-white/[0.08] p-3">
        {user && expanded ? (
          <div className="px-3 py-2.5 mb-2 rounded-lg bg-white/[0.05]">
            <p className="text-xs font-medium text-slate-200 truncate">{user.full_name}</p>
            <p className="text-[11px] text-muted-foreground truncate">{user.company_name}</p>
          </div>
        ) : null}
        <button
          onClick={handleLogout}
          className={cn(
            "flex items-center rounded-lg py-2 text-[13px] w-full text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-all duration-150",
            expanded ? "justify-start gap-3 px-3" : "justify-center px-2"
          )}
          data-testid="logout-btn"
        >
          <LogOut className="h-[18px] w-[18px]" strokeWidth={1.75} />
          {expanded ? <span className="font-medium">Çıkış Yap</span> : null}
        </button>
        {expanded ? <div className="mt-3 px-3 text-[10px] text-muted-foreground">&copy; {new Date().getFullYear()} obihub</div> : null}
      </div>
    </div>
  );
}
